import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Spin,
  Button,
  Tag,
  message,
  Descriptions,
  Card,
  Space,
  Modal,
} from "antd";
import {
  CalendarOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  InfoCircleOutlined,
  CameraOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { useAuth } from "../../contexts/AuthContext";
import EditEventModal from "./EditEventModal";
import UpdateImageModal from "./UpdateImageModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PageContainer = styled.div`
  margin-left: 280px;
  padding: 40px;
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const ContentCard = styled(Card)`
  max-width: 1200px;
  margin: 0 auto;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const TitleSection = styled.div`
  flex: 1;
`;

const EventTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #3c2f61;
  margin: 0 0 10px 0;
`;

const ActionButtons = styled(Space)`
  @media (max-width: 768px) {
    width: 100%;
    
    button {
      flex: 1;
    }
  }
`;

const BackButton = styled(Button)`
  margin-bottom: 20px;
`;

const EventImage = styled.div`
  width: 100%;
  height: 400px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  margin-bottom: 30px;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 120px;
  font-weight: bold;
`;

const UpdateImageButton = styled(Button)`
  position: absolute;
  top: 16px;
  right: 16px;
  background-color: rgba(60, 47, 97, 0.9);
  border: none;
  color: white;

  &:hover {
    background-color: rgba(60, 47, 97, 1) !important;
    color: white !important;
  }
`;

const StatusTag = styled(Tag)`
  font-size: 14px;
  padding: 6px 16px;
  border-radius: 20px;
`;

const SectionTitle = styled.h3`
  color: #3c2f61;
  font-size: 20px;
  font-weight: 600;
  margin: 30px 0 16px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DescriptionText = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.8;
  white-space: pre-wrap;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
`;

const EventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchEventDetail();
  }, [eventId]);

  const fetchEventDetail = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/companies/events/${eventId}/detail`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setEvent(response.data.data);
    } catch (error) {
      console.error("Error fetching event details:", error);
      message.error("Failed to load event details");
      navigate("/events");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await axios.delete(
        `${BASE_URL}/companies/events/${eventId}/delete`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      message.success("Event deleted successfully!");
      navigate("/events");
    } catch (error) {
      console.error("Error deleting event:", error);
      message.error(error.response?.data?.detail || "Failed to delete event");
    } finally {
      setDeleting(false);
      setDeleteModalVisible(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('en-US', options);
  };

  const formatAmount = (amount, currencyCode) => {
    return `${currencyCode} ${amount.toLocaleString()}`;
  };

  const isUpcoming = event && new Date(event.event_date) > new Date();
  const isPast = event && new Date(event.event_date) < new Date();

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <Spin size="large" tip="Loading event details..." />
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <PageContainer>
      <BackButton
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/events")}
        size="large"
      >
        Back to Events
      </BackButton>

      <ContentCard>
        <HeaderSection>
          <TitleSection>
            <EventTitle>{event.name}</EventTitle>
            {isUpcoming && <StatusTag color="green">Upcoming Event</StatusTag>}
            {isPast && <StatusTag color="default">Past Event</StatusTag>}
            {!event.available && <StatusTag color="red">Cancelled</StatusTag>}
          </TitleSection>

          <ActionButtons>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => setEditModalVisible(true)}
              size="large"
              style={{ backgroundColor: "#3c2f61", borderColor: "#3c2f61" }}
            >
              Edit Event
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => setDeleteModalVisible(true)}
              size="large"
            >
              Delete
            </Button>
          </ActionButtons>
        </HeaderSection>

        <EventImage>
          {event.image_url ? (
            <img src={event.image_url} alt={event.name} />
          ) : (
            <ImagePlaceholder>
              {event.name.charAt(0).toUpperCase()}
            </ImagePlaceholder>
          )}
          <UpdateImageButton
            icon={<CameraOutlined />}
            onClick={() => setImageModalVisible(true)}
            size="large"
          >
            Update Image
          </UpdateImageButton>
        </EventImage>

        <Descriptions
          bordered
          column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
          size="large"
        >
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined /> Event Date
              </>
            }
            span={2}
          >
            <strong>{formatDate(event.event_date)}</strong>
          </Descriptions.Item>

          {event.physical_location && (
            <Descriptions.Item
              label={
                <>
                  <EnvironmentOutlined /> Location
                </>
              }
              span={2}
            >
              {event.physical_location}
            </Descriptions.Item>
          )}

          <Descriptions.Item
            label={
              <>
               Amount (per person)
              </>
            }
            span={2}
          >
            <strong style={{ fontSize: "18px", color: "#3c2f61" }}>
              {formatAmount(event.amount, event.currency_code || "KES")}
            </strong>
          </Descriptions.Item>
        </Descriptions>

        {event.description && (
          <>
            <SectionTitle>
              <InfoCircleOutlined />
              About This Event
            </SectionTitle>
            <DescriptionText>{event.description}</DescriptionText>
          </>
        )}

        {event.payment_info && (
          <>
            <SectionTitle>
             
              Payment Information
            </SectionTitle>
            <DescriptionText>{event.payment_info}</DescriptionText>
          </>
        )}

        {event.created_at && (
          <div style={{ marginTop: "30px", fontSize: "13px", color: "#999" }}>
            Created on {new Date(event.created_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        )}
      </ContentCard>

      <EditEventModal
        visible={editModalVisible}
        event={event}
        onCancel={() => setEditModalVisible(false)}
        onSuccess={() => {
          setEditModalVisible(false);
          fetchEventDetail();
        }}
      />

      <UpdateImageModal
        visible={imageModalVisible}
        event={event}
        onCancel={() => setImageModalVisible(false)}
        onSuccess={() => {
          setImageModalVisible(false);
          fetchEventDetail();
        }}
      />

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        eventName={event.name}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModalVisible(false)}
        loading={deleting}
      />
    </PageContainer>
  );
};

export default EventDetailPage;